import {AppAccessDeliveryAudit, AppAccessPackage, AppAccessTarget} from "@ots-frontend/ots-data";
import {DocumentArrowDownIcon, TrashIcon} from "@heroicons/react/20/solid";
import {OtsActionButton} from "../components/OtsButton";
import {StatusPill} from "../components/StatusPill";
import {AppAccessDeliveryAuditTable} from "./AppAccessDeliveryAuditTable";
import {AppAccessEmailDeliveryForm} from "./AppAccessEmailDeliveryForm";
import {AppAccessSmsDeliveryForm} from "./AppAccessSmsDeliveryForm";
import {AppAccessTargetCard} from "./AppAccessTargetCard";
import {appAccessCardClass, canDeliverAppAccessPackage, getAppAccessPackageDeliveryStatus, getAppAccessPurposeLabel} from "./app-access-ui";

export type AppAccessPackageDetailsProps = {
  appAccessPackage?: AppAccessPackage;
  audits?: AppAccessDeliveryAudit[];
  isAuditLoading?: boolean;
  isEmailSending?: boolean;
  isSmsSending?: boolean;
  smsDeliveryEnabled?: boolean;
  canManage?: boolean;
  canDistributeCredentials?: boolean;
  canSendEmail?: boolean;
  canSendSms?: boolean;
  onExportPdf: (appAccessPackage: AppAccessPackage) => void | Promise<void>;
  onRevokePackage: (appAccessPackage: AppAccessPackage) => void | Promise<void>;
  onCopyTargetLink: (target: AppAccessTarget) => void | Promise<void>;
  onExportTargetQr: (target: AppAccessTarget) => void | Promise<void>;
  onRevokeTarget: (target: AppAccessTarget) => void | Promise<void>;
  onSendEmail: (recipients: string[], message?: string) => boolean | Promise<boolean>;
  onSendSms: (recipients: string[], message?: string) => boolean | Promise<boolean>;
  className?: string;
};

/** Presentational package workspace composed from independently reusable delivery controls. */
export const AppAccessPackageDetails = ({
  appAccessPackage,
  audits = [],
  isAuditLoading = false,
  isEmailSending = false,
  isSmsSending = false,
  smsDeliveryEnabled = false,
  canManage = true,
  canDistributeCredentials = true,
  canSendEmail = true,
  canSendSms = true,
  onExportPdf,
  onRevokePackage,
  onCopyTargetLink,
  onExportTargetQr,
  onRevokeTarget,
  onSendEmail,
  onSendSms,
  className,
}: AppAccessPackageDetailsProps) => {
  const canDeliver = appAccessPackage ? canDeliverAppAccessPackage(appAccessPackage) : false;
  const deliveryStatus = appAccessPackage ? getAppAccessPackageDeliveryStatus(appAccessPackage) : undefined;
  const canManuallyDistribute = appAccessPackage?.mode === "CONFIGURATION" || canDistributeCredentials;

  return <section className={[appAccessCardClass, className].filter(Boolean).join(" ")} aria-live="polite">
    {!appAccessPackage ? (
      <p className="text-sm text-[var(--ots-text-muted)]">Select a setup to send it or review its delivery activity.</p>
    ) : (
      <>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-[var(--ots-secondary)]">{appAccessPackage.name}</h2>
              {deliveryStatus && <StatusPill tone={deliveryStatus.tone}>{deliveryStatus.label}</StatusPill>}
            </div>
            <p className="text-sm text-[var(--ots-text-muted)]">
              {getAppAccessPurposeLabel(appAccessPackage.purpose)}
              {appAccessPackage.defaultRegionName ? ` · ${appAccessPackage.defaultRegionName}` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            {canManuallyDistribute && <OtsActionButton
              size="compact"
              icon={<DocumentArrowDownIcon/>}
              disabled={!canDeliver}
              title={canDeliver ? undefined : "A printable PDF cannot be generated because one or more setup links are unavailable."}
              onClick={() => void onExportPdf(appAccessPackage)}>
              Printable PDF
            </OtsActionButton>}
            {canManage && <OtsActionButton size="compact" variant="text" icon={<TrashIcon/>} disabled={Boolean(appAccessPackage.revokedDttm)} onClick={() => void onRevokePackage(appAccessPackage)}>Revoke</OtsActionButton>}
          </div>
        </div>

        {!canDeliver && (
          <p className="mt-4 rounded-lg bg-[var(--ots-warning-container)] px-3 py-2 text-sm text-[var(--ots-warning)]">
            One or more setup links are expired, already used, or revoked. Create a new setup before sending or printing the complete package.
          </p>
        )}

        <div className="mt-5">
          <h3 className="font-semibold text-[var(--ots-secondary)]">Share this setup</h3>
          <p className="mt-1 text-sm text-[var(--ots-text-muted)]">
            Copy a link for messaging, show or download a QR code, or send it by email{smsDeliveryEnabled ? " or SMS" : ""} below.
          </p>
        </div>
        <div className="mt-4 space-y-3">
          {appAccessPackage.targets.map((target) => (
            <AppAccessTargetCard
              key={target.id}
              target={target}
              mode={appAccessPackage.mode}
              canDistribute={canManuallyDistribute}
              canRevoke={canManage}
              onCopyLink={onCopyTargetLink}
              onExportQr={onExportTargetQr}
              onRevoke={onRevokeTarget}/>
          ))}
        </div>

        {(canSendEmail || (canSendSms && smsDeliveryEnabled)) && <div className={`mt-6 grid gap-4 ${canSendEmail && canSendSms && smsDeliveryEnabled ? "xl:grid-cols-2" : ""}`}>
          {canSendEmail && <AppAccessEmailDeliveryForm
            isSending={isEmailSending}
            disabled={!canDeliver}
            onSend={onSendEmail}/>}
          {canSendSms && smsDeliveryEnabled && (
            <AppAccessSmsDeliveryForm
              isSending={isSmsSending}
              disabled={!canDeliver}
              onSend={onSendSms}/>
          )}
        </div>}

        <details className="mt-6 border-t border-[var(--ots-outline-variant)] pt-4">
          <summary className="cursor-pointer text-sm font-semibold text-[var(--ots-primary)]">Delivery activity</summary>
          <div className="mt-3"><AppAccessDeliveryAuditTable audits={audits} isLoading={isAuditLoading}/></div>
        </details>
      </>
    )}
  </section>;
};
